import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 15, 2026";

const SECTIONS = [
  {
    id: 1,
    title: "Introduction",
    paragraphs: [
      'GEO Maps ("we", "us", "our") is a driving-assistance app that combines standard 2D turn-by-turn directions with an Augmented Reality (AR) camera overlay. This Privacy Policy explains, in plain language, what personal data we collect from you, how we use it, who we share it with, and the choices you have over it.',
      "By using the app you agree to this policy. If you do not agree, please stop using the app and contact us to have your account deleted.",
    ],
  },
  {
    id: 2,
    title: "Real-time GPS Tracking",
    intro:
      "The core purpose of the app is turn-by-turn navigation. To make that work we continuously read your device's location while a trip is active.",
    items: [
      [
        "Active-trip capture",
        "Your precise GPS coordinates, heading and speed are captured while a route is running — typically every 2 seconds — so we can render the driver marker, snap it to the road, detect off-route drift, and drive audio guidance.",
      ],
      [
        "Traffic & POIs",
        "Location is also used to fetch traffic-aware alternate routes, colour-coded traffic segments, and along-route point-of-interest results (petrol pumps, restaurants, rest areas, hotels, washrooms).",
      ],
      [
        "Automatic stop",
        'Location tracking stops automatically the moment you tap "Exit navigation" or the app goes into a state where no trip is active.',
      ],
      [
        "Live Location Share",
        "If — and only if — you explicitly start a Live Location Share, a temporary anonymous share ID is created and your position is broadcast to anyone who opens the resulting link. Live shares auto-expire after the duration you choose (max 24 hours) and can be revoked from the app at any time.",
      ],
      [
        "No background tracking",
        "We do not track your background location. When the app is not in the foreground with an active trip, we do not read your GPS.",
      ],
    ],
  },
  {
    id: 3,
    title: "AR Camera Usage",
    intro:
      "The AR View feature overlays a rotating 3D directional arrow on the phone's live rear-camera preview so you can see the turn on the actual road, not just on the map.",
    items: [
      [
        "Preview only",
        "The camera feed is used only as a live preview inside the AR pane on your device. It is never recorded, saved to disk, uploaded to our servers, or transmitted to any third party.",
      ],
      [
        "On-device processing",
        "Frames stay entirely on your phone. Once you leave the AR view or exit the trip, the camera stream is stopped immediately.",
      ],
      [
        "Permission control",
        "You are asked to grant camera permission the first time you open AR View. You can revoke it at any time from your device settings; the rest of the app continues to work without AR.",
      ],
      [
        "In short",
        "No image, video, or camera frame ever leaves your device.",
      ],
    ],
  },
  {
    id: 4,
    title: "Third-Party Services",
    intro:
      "We use a small, purpose-built set of third-party services to power core features of GEO Maps. When you use these features the relevant data below is sent to the corresponding provider under their own privacy terms.",
    items: [
      [
        "Google Maps Platform",
        "Places, Directions API, Routes API v2, Street View Static, and the Maps SDK for Android/iOS. Used for search autocomplete, directions, traffic-aware polylines, and rendering the 2D map. We share your origin/destination and current location as required for these calls. See Google's Privacy Policy at policies.google.com/privacy.",
      ],
      [
        "Emergent-managed Google Social Login",
        "Used only if you choose to sign in with Google. We receive your name, email and avatar URL from Google to create your account. We do not receive your Google password.",
      ],
      [
        "MongoDB Atlas",
        "Hosts our encrypted application database (user profiles, saved routes, trip history, saved places). No third-party analytics, ad-tech or fingerprinting SDKs are embedded in the app.",
      ],
    ],
  },
  {
    id: 5,
    title: "What Data We Collect",
    intro:
      "To keep the surface area small and predictable, here is the full list of personal data stored in our own database:",
    items: [
      [
        "Account info",
        "Email, hashed password (bcrypt), optional display name, avatar URL if you signed in with Google.",
      ],
      [
        "Saved routes and saved places",
        "The labels, addresses and coordinates you explicitly save.",
      ],
      [
        "Trip history",
        "Trips you complete: origin/destination, polyline, distance, duration, vehicle used, estimated fuel cost and completion timestamp.",
      ],
      [
        "Fuel-price preferences",
        "The per-litre prices you enter for Petrol / Diesel / CNG, used to compute trip cost estimates.",
      ],
      [
        "Live-share metadata",
        "The temporary share IDs you create, their expiry, and the most recent location ping. Automatically purged 24 h after expiry via a TTL index.",
      ],
    ],
  },
  {
    id: 6,
    title: "Data Security",
    intro: "Your data is treated as sensitive by default.",
    items: [
      [
        "Encryption in transit",
        "All traffic between the app and our servers is encrypted in transit over HTTPS (TLS 1.2+).",
      ],
      [
        "Password hashing",
        "Passwords are stored as bcrypt hashes with a per-user salt. Even we cannot read your plaintext password.",
      ],
      [
        "Encryption at rest",
        "The database is hosted on managed cloud infrastructure with encryption-at-rest and role-scoped access controls.",
      ],
      [
        "No selling of data",
        "We do not sell, rent or trade your personal data to third parties. We do not use your data to train third-party advertising or profiling models.",
      ],
      [
        "Self-service deletion",
        "You can delete your account, trip history or saved places at any time from the Profile screen. Live shares can be revoked with a single tap. Deleted records are removed from the primary database immediately; encrypted backups are rotated out within 30 days.",
      ],
    ],
  },
  {
    id: 7,
    title: "Your Rights",
    paragraphs: [
      "Depending on your jurisdiction (GDPR, CCPA, DPDP, and similar regimes), you have the right to access, correct, export or delete the personal data we hold about you. To exercise any of these rights, email us at the address below and we will respond within 30 days.",
    ],
  },
  {
    id: 8,
    title: "Changes to This Policy",
    paragraphs: [
      "If we materially change how we handle your data we will update this page, bump the effective date at the top, and — for account-holders — surface a one-time in-app notice on next launch so you can review the changes before continuing to use the app.",
    ],
  },
];

export default function GeoMapsPrivacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="geomaps-privacy-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 antialiased selection:bg-[#4CAF50] selection:text-[#04140a]"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="geomaps-logo-home" className="flex items-center">
            <img
              src="/geonetix-logo-full.webp"
              alt="GeoNetix"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="geomaps-back-home"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-100 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 gx-grid-bg opacity-40" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#00B4D8]/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4"
          >
            / GEO Maps · Legal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            GEO Maps <span className="gx-text-gradient">Privacy Policy</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-2xl text-slate-300 leading-relaxed"
          >
            GEO Maps is a driving-assistance app that combines standard 2D
            turn-by-turn directions with an Augmented Reality (AR) camera
            overlay. This policy explains — in plain language — what personal
            data we collect, how it&apos;s used, and the choices you have over
            it.
          </motion.p>
          <p className="mt-6 font-mono-gx text-xs uppercase tracking-[0.2em] text-slate-500">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-14 md:py-20">
        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`geomaps-section-${s.id}`}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-mono-gx text-2xl font-bold text-[#4CAF50]/70">
                  {String(s.id).padStart(2, "0")}
                </span>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                  {s.title}
                </h2>
              </div>

              {s.intro && (
                <p className="text-slate-400 leading-relaxed mb-5">{s.intro}</p>
              )}

              {s.paragraphs &&
                s.paragraphs.map((p, i) => (
                  <p key={i} className="text-slate-400 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

              {s.items && (
                <ul className="space-y-3">
                  {s.items.map(([term, desc], i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                    >
                      <span className="font-semibold text-white">{term}: </span>
                      <span className="text-slate-400">{desc}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div
          data-testid="geomaps-contact-card"
          className="mt-12 rounded-3xl border border-[#00B4D8]/25 bg-gradient-to-r from-[#00B4D8]/10 to-transparent p-8 text-center"
        >
          <p className="font-mono-gx text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-3">
            / Contact
          </p>
          <p className="text-slate-300 mb-6">
            For privacy questions or data requests, please contact:
          </p>
          <a
            data-testid="geomaps-privacy-mailto"
            href="mailto:privacy@geonetix.live"
            className="inline-flex items-center gap-2 rounded-full bg-[#4CAF50] px-7 py-3.5 text-sm font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow"
          >
            privacy@geonetix.live
          </a>
        </div>
      </main>

      {/* Bottom bar */}
      <footer className="border-t border-white/10 bg-[#050d1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img
            src="/geonetix-logo-full.webp"
            alt="GeoNetix"
            className="h-9 w-auto object-contain"
          />
          <div className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} GeoNetix. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
